/**
 * What a Forget door says, and whether one is in flight — shared by every door
 * so none of them promises something softer, or louder, than the others.
 *
 * The sentences follow the outcome `core/forget.ts` returns, not the press: a
 * door used to say "Forgotten." the instant it was pressed, and the browser's
 * refusal arrived afterwards as a separate toast that named nothing. Now the one
 * sentence is said once the store has answered, and it is about what the store
 * answered.
 */

import { useSyncExternalStore } from 'react'
import {
  forgetting,
  forgettingEverything,
  subscribeForgetting,
  type ForgetAllOutcome,
  type ForgetOutcome,
  type ForgetRefusal,
} from '../core/forget'

export type ToastTone = 'info' | 'warn' | 'bad'

/** How a refusal is worded for one exploration, and for every one. Two sets
 *  rather than one sentence with a noun spliced in: "the place them is kept"
 *  is what splicing produced. */
const REFUSED: Record<
  'one' | 'all',
  Record<Exclude<ForgetRefusal, 'busy'>, (said: string) => { text: string; tone: ToastTone }>
> = {
  one: {
    storage: (said) => ({
      text: `Not forgotten — this browser refused to delete it${said}. Nothing was removed; it is still in your library.`,
      tone: 'bad',
    }),
    unavailable: (said) => ({
      text: `Not forgotten — the place it is kept could not be reached${said}. Nothing was removed; try again when it can be.`,
      tone: 'bad',
    }),
    conflict: (said) => ({
      text: `Not forgotten — it changed since it was shown${said}. Nothing was removed; look at it again before forgetting it.`,
      tone: 'warn',
    }),
    forbidden: (said) => ({
      text: `Not forgotten — you can’t delete it${said}. Nothing was removed.`,
      tone: 'warn',
    }),
  },
  all: {
    storage: (said) => ({
      text: `Not forgotten — this browser refused to delete them${said}. Nothing was removed; they are all still in your library.`,
      tone: 'bad',
    }),
    unavailable: (said) => ({
      text: `Not forgotten — the place they are kept could not be reached${said}. Nothing was removed; try again when it can be.`,
      tone: 'bad',
    }),
    conflict: (said) => ({
      text: `Not forgotten — some changed since they were shown${said}. Nothing was removed; look at them again before forgetting them.`,
      tone: 'warn',
    }),
    forbidden: (said) => ({
      text: `Not forgotten — you can’t delete them all${said}. Nothing was removed.`,
      tone: 'warn',
    }),
  },
}

/** Why nothing was removed, in words, with the store's own detail when it gave
 *  one. Null for `busy`: the lock has already said its sentence. */
function refusedLine(
  reason: ForgetRefusal,
  detail: string | undefined,
  how: 'one' | 'all',
): { text: string; tone: ToastTone } | null {
  if (reason === 'busy') return null
  return REFUSED[how][reason](detail ? ` (${detail})` : '')
}

/** The sentence for one exploration's Forget, or null when there is nothing to
 *  add to what has already been said. */
export function forgetLine(out: ForgetOutcome): { text: string; tone: ToastTone } | null {
  switch (out.kind) {
    case 'forgotten':
      return { text: out.had ? 'Forgotten.' : 'It was already gone.', tone: 'info' }
    case 'page-only':
      return {
        text: 'Forgotten from this tab. Storage could not be read, so a stored copy, if there is one, is untouched and comes back when it can be read.',
        tone: 'warn',
      }
    case 'kept':
      return refusedLine(out.reason, out.detail, 'one')
  }
}

/**
 * What a confirm says before one exploration is forgotten — the sidebar's and the
 * library's, from here, so neither door promises less than the other (D335). The
 * sidebar's confirm used to leave out the recorded-results clause the library
 * printed, and the sidebar is the door used most.
 *
 * `name` where the confirm stands in for the row and so has to say which one; the
 * library prints the row above its confirm and asks only "Forget it?".
 */
export function forgetSentence(o: { name?: string; outcomes?: number; isOpen: boolean }): string {
  const ask = o.name ? `Forget “${o.name}”?` : 'Forget it?'
  return (
    `${ask} The graph, its checks and any recorded result go with it, and this cannot be undone.` +
    // A graph can be reasoned out again; a recorded result is a trip to a lab.
    (o.outcomes
      ? ` It holds ${o.outcomes} recorded result${o.outcomes === 1 ? '' : 's'} — the one thing here that another pass cannot reproduce.`
      : '') +
    (o.isOpen ? ' It is the one open, so the canvas clears too.' : '')
  )
}

/**
 * What the Forget-everything confirm says. `keysKept` when a provider key is
 * stored: the keys are deliberately not swept (`LS` in `core/store.ts`), and a
 * reader using this as "clean up before I leave" on a borrowed machine would
 * otherwise walk away with them still there (D335).
 */
export function forgetAllSentence(o: {
  count: number
  withResults: boolean
  keysKept: boolean
}): string {
  return (
    `Forget all ${o.count}? Every stored graph on this device goes` +
    (o.withResults ? ', with the recorded results in them' : '') +
    ', and the one that is open. This cannot be undone.' +
    (o.keysKept ? ' Your API keys stay; remove them under Settings → Providers.' : '')
  )
}

/** The sentence for Forget everything. `keysKept` adds what was NOT swept, for
 *  `forgetAllSentence`'s reason; the door that says it offers the way there. */
export function forgetAllLine(
  out: ForgetAllOutcome,
  keysKept = false,
): { text: string; tone: ToastTone; keysKept?: true } | null {
  switch (out.kind) {
    case 'forgotten':
      return keysKept
        ? {
            text: 'Every stored exploration forgotten. Your API keys are still stored in this browser.',
            tone: 'info',
            keysKept: true,
          }
        : { text: 'Every stored exploration forgotten.', tone: 'info' }
    case 'page-only':
      return {
        text: 'Forgotten from this tab. Storage could not be read, so the stored explorations are untouched and come back when it can be read.',
        tone: 'warn',
      }
    case 'kept':
      return refusedLine(out.reason, out.detail, 'all')
  }
}

/** Whether a Forget of this exploration — or of everything — is in flight, so
 *  the door can show the press as pending and refuse a second one. False for
 *  no exploration, which is a door with nothing armed. */
export function useForgetting(id: string | null): boolean {
  return useSyncExternalStore(
    subscribeForgetting,
    () => id !== null && forgetting(id),
    () => false,
  )
}

/** Whether the Forget-everything door is in flight. */
export function useForgettingEverything(): boolean {
  return useSyncExternalStore(subscribeForgetting, forgettingEverything, () => false)
}

/**
 * A request to open the library with Forget everything already armed — the
 * palette's door to the act, never the act (D335).
 *
 * The palette's row used to forget everything on one Enter, and "for" on an
 * empty canvas ranked it first and alone: a reader reaching for Fork or Focus by
 * prefix wiped the library. Now the row asks, and the library's own confirm —
 * the count, the recorded-results clause, the pending state — answers. Held here
 * beside the other forget state rather than in the store, as `forget.ts` holds
 * its in-flight set: it is one flag with one reader, consumed the moment the
 * sheet is up, and the ledger's ask (`ledgerAsk`) is the same shape.
 */
let allAsked = false
const askHeard = new Set<() => void>()
const askChanged = () => askHeard.forEach((f) => f())

export function askForgetAll(): void {
  allAsked = true
  askChanged()
}

export function clearForgetAllAsk(): void {
  if (!allAsked) return
  allAsked = false
  askChanged()
}

/** Whether the library has been asked to arm Forget everything. */
export function useForgetAllAsk(): boolean {
  return useSyncExternalStore(
    (f) => {
      askHeard.add(f)
      return () => askHeard.delete(f)
    },
    () => allAsked,
    () => false,
  )
}
